'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { ErrorState } from '@/components/v/error-state'
import { useI18n } from '@/i18n/context'
import { fetchGroupByChat, resolveLaunchRoute } from '@/lib/telegram/launch-route'
import { markReplace } from '@/lib/telegram/nav-stack'
import { groupsQuery, todayQuery } from '@/lib/v/tab-queries'

export default function Launch() {
  const router = useRouter()
  const { t } = useI18n()
  const qc = useQueryClient()
  const [mode, setMode] = useState<'loading' | 'no-telegram'>('loading')

  useEffect(() => {
    const wa = window.Telegram?.WebApp
    const initData = wa?.initData ?? ''
    const devMode = process.env.NEXT_PUBLIC_DEV_MODE === '1' && !!process.env.NEXT_PUBLIC_DEV_USER_ID

    if (!initData && !devMode) {
      setMode('no-telegram')
      return
    }

    if (!initData && devMode) {
      warmGroups()
      markReplace()
      router.replace('/groups' as never)
      return
    }

    wa?.ready?.()
    wa?.expand?.()

    // Language: Providers starts from Telegram's language_code and LangSync
    // (TelegramShell) applies the one picked in the bot, so nothing to do here.

    // start_param deep link first, then the group the app was opened from
    // (attachment-menu launches carry chat.id), else the group picker.
    let cancelled = false
    resolveLaunchRoute(wa?.initDataUnsafe, { groupByChat: fetchGroupByChat(`tma ${initData}`) }).then((route) => {
      if (cancelled) return
      // Only the picker needs the groups list — don't spend a request on it
      // when a deep link goes straight into a group.
      if (route === '/groups') warmGroups()
      // Start Today's request now instead of after the route hydrates.
      const home = /^\/g\/([a-f0-9]{24})$/.exec(route)
      if (home) void qc.prefetchQuery(todayQuery(home[1]))
      markReplace()
      router.replace(route as never)
    })
    return () => {
      cancelled = true
    }

    function warmGroups() {
      void qc.prefetchQuery(groupsQuery())
    }
  }, [router, qc])

  if (mode === 'no-telegram') {
    return (
      <ErrorState
        variant="forbidden"
        title={t('noTelegramTitle')}
        body={t('noTelegramBody')}
        actionLabel={t('openBotAction')}
        href="https://t.me/QuotLyBot"
      />
    )
  }

  // The app hydrates and redirects in the same frame on a warm cache; a
  // splash would only flash. Render nothing — the layout's bg keeps the
  // background consistent during the sub-perceptible redirect window.
  return null
}
